import { of as observableOf, Observable, forkJoin } from 'rxjs'
import { Component, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import {
  MarketODataService,
  IMarket,
  ProviderODataService,
  UserODataService
} from '@exchange/domain/.'
import { UtilService } from '@exchange/services/.'
import { TabsetComponent } from 'ngx-bootstrap/tabs'
import { ToastrService } from 'ngx-toastr'
import { take } from 'rxjs/operators'
import { Option, AppSelectComponent } from 'common-components'
import { IUser } from 'ng-client-auth'
import { LocationCoverageTypes } from '@exchange/app/domain/system/locationCoverageType'
import { PagedResult } from 'common-services'
import { IMarketChannel } from '@exchange/app/domain/system/marketChannel'

@Component({
  selector: 'app-market-edit',
  templateUrl: './market-edit.component.html',
  standalone: false
})
export class MarketEditComponent implements OnInit {
  @ViewChild('tabs') tabs: TabsetComponent
  @ViewChild('operationsDirectorList', { static: true })
  operationsDirectorList: AppSelectComponent

  title = 'Edit Market'
  model: IMarket = { MarketChannel: {} } as IMarket
  operationsLeaderUsers: Option[] = []

  busy: any = null
  marketId: number

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private odata: MarketODataService,
    private providerService: ProviderODataService,
    private userService: UserODataService,
    public util: UtilService,
    private toastr: ToastrService
  ) {}

  initializeAdd() {
    this.title = 'Add Market'
    const market = {
      Id: 0,
      Name: '',
      MarketChannel: {
        AdmissionChannelLink: null,
        RehabAdmissionChannelLink: null,
        ScreeningChannelLink: null
      } as IMarketChannel,
      OrganizationName: null,
      OrganizationPhoneNumber: null,
      OrganizationPhoneNumber1Id: null,
      OrganizationPhoneNumber1: null,
      LocationCoverageTypeId: null,
      LocationCoverageDistance: null,
      PeopleSoftBusinessUnit: null,
      IsActive: false
    } as IMarket
    const paged = {
      skipped: 0,
      rowCount: 1,
      data: [market]
    } as PagedResult<IMarket>

    return observableOf(paged)
  }

  initializeEdit() {
    return this.initializeModel()
  }

  private initializeModel(): Observable<PagedResult<IMarket>> {
    return this.odata
      .query()
      .filter(`Id eq ${this.marketId}`)
      .expand('MarketChannel')
      .exec()
  }

  ngOnInit() {
    this.marketId = +this.route.snapshot.params['id']

    this.route.params.subscribe(() => {
      const fn = this.marketId ? this.initializeEdit.bind(this) : this.initializeAdd.bind(this)

      forkJoin({
        users: this.userService.customFunction(null, 'Default.OperationsLeaderUsers'),
        initializedFunction: fn()
      }).subscribe(({ users, initializedFunction }: { users: any; initializedFunction: any }) => {
        this.model = initializedFunction.data[0]
        this.operationsLeaderUsers = users.value.map(
          (olu: IUser) => new Option(olu.Id, `${olu.FirstName} ${olu.LastName}`, olu)
        )
        this.operationsDirectorList.active = this.operationsLeaderUsers.filter(
          user => user.id === this.model.OperationsDirectorUserId
        )
      })
    })
  }

  onSubmit(): void {
    const errors = []

    if (!this.model.Name) {
      errors.push('Name is required.')
    }

    this.providerService
      .query()
      .filter(`MarketId eq ${this.marketId}`)
      .exec()
      .pipe(take(1))
      .subscribe(providers => {
        if (!this.model.IsActive && providers.rowCount > 0) {
          errors.push('Market cannot be inactivated until all providers are removed.')
        }

        this.toastr.clear()
        if (errors.length > 0) {
          this.toastr.error(errors.join('<br />'))
          return
        }

        const model = {
          Id: +this.model.Id || undefined,
          Name: this.model.Name,
          IsActive: this.model.IsActive,
          MarketChannel: this.model.MarketChannel,
          OrganizationName: this.model.OrganizationName,
          OrganizationPhoneNumber: this.util.sanitizePhoneNumber(
            this.model.OrganizationPhoneNumber
          ),
          OrganizationPhoneNumber1Id: this.model.OrganizationPhoneNumber1Id,
          OrganizationPhoneNumber1: this.model.OrganizationPhoneNumber1,
          OperationsDirectorUserId: this.model.OperationsDirectorUserId,
          LocationCoverageTypeId: this.model.LocationCoverageTypeId,
          LocationCoverageDistance:
            this.model.LocationCoverageTypeId === LocationCoverageTypes.ZipCode
              ? null
              : this.model.LocationCoverageDistance,
          PeopleSoftBusinessUnit: this.model.PeopleSoftBusinessUnit
        }

        const observable = model.Id
          ? this.odata.put(model as IMarket, model.Id).exec()
          : this.odata.post(model as IMarket).exec()

        this.busy = observable.subscribe({
          next: result => {
            this.busy = null

            if (this.model.Id) {
              // This was an update.
              this.backToList('Market was saved successfully.')
            } else {
              // This was a create.
              this.toastr.success('Market was created successfully.')

              this.model.Id = this.marketId = +result.Id
              this.initializeModel()

              setTimeout(() => {
                this.router.navigate(['/system', 'markets', this.model.Id])
                this.title = 'Edit Market'
                this.tabs.tabs[1].active = true
              }, 1)
            }
          },
          error: this.showError.bind(this)
        })
      })
  }

  showError(response): void {
    this.busy = null

    const msg = this.util.getErrorString(response)

    this.toastr.error(msg)
  }

  backToList(successMessage: string = null) {
    this.router.navigate(['/system', 'markets'], {
      queryParams: { successMessage }
    })
  }

  selectedOperationsDirector = (value: any = null) => {
    this.model.OperationsDirectorUserId = (value && value.id) || null
  }

  locationTypeChanged(option: any) {
    this.model.LocationCoverageTypeId = option.id
  }

  locationTypeCleared() {
    this.model.LocationCoverageTypeId = null
  }
}
